





























































































import { Component, Prop, Vue } from 'vue-property-decorator';

import Table from '@/components/molecules/Table/Table.vue';
import TableCell from '@/components/molecules/TableCell/TableCell.vue';
import TableHeader from '@/components/molecules/TableHeader/TableHeader.vue';
import TableRow from '@/components/molecules/TableRow/TableRow.vue';
import FilterTag from '@/componentsV2/molecules/FilterTag/FilterTag.vue';
import SectionData from '@/componentsV2/organisms/SectionData/SectionData.vue';
import Trigger from '@/componentsV2/molecules/Trigger/Trigger.vue';
import TableCard from '@/componentsV2/molecules/TableCard/TableCard.vue';

import { IPeriod } from '@/models/period';
import {
  EActivityStatus,
  ICustomerActivity,
  IInitiativeFilter
} from '@/models/performance';

import PerformanceOverviewService from '@/api/PerformanceOverview';

import PerformanceUtils from '@/utils/PerformanceUtils';
import PaginationUtils, {
  EPaginationSortDir,
  IPagination,
  IPaginationTableHeader
} from '@/utils/PaginationUtils';
import { applyPerformanceDataFilter } from '@/views/Performance/flag-helpers';
import flagKeys from '@/constants/flagKeys';
import DateUtils from '@/utils/DateUtils';
import eventBus from '@/utils/eventBus';

interface ITableRow {
  date: string;
  action: string;
  initiativeSlug: string;
  initiativeLabel: string;
}

@Component({
  components: {
    Table,
    TableCell,
    TableHeader,
    TableRow,
    FilterTag,
    SectionData,
    Trigger,
    TableCard
  }
})
export default class PerformanceCustomerDetailActivities extends Vue {
  @Prop(String) customerId!: string;
  @Prop(String) title!: string;

  performanceOverviewService = new PerformanceOverviewService();
  period: IPeriod = PerformanceUtils.defaultPeriod();
  initiatives: IInitiativeFilter[] = [];
  activities: ITableRow[] = [];
  headers: Record<string, IPaginationTableHeader> = {
    createdAt: {
      label: 'Date',
      sortable: true,
      sortDir: EPaginationSortDir.DESC
    },
    action: {
      label: 'Action'
    },
    initiative: {
      label: 'Initiative',
      sortable: true
    }
  };
  pagination: IPagination = PaginationUtils.defaultPagination();
  filters: Record<string, any> = {
    ...PerformanceUtils.defaultPeriodForFilter()
  };
  firstRequestIsLoading = false;

  get isMobileDevice() {
    return this.$mobileCheck();
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get businessReferralIsActive() {
    return this.flags.includes(flagKeys.businessReferral);
  }

  get filterGATracking() {
    return {
      eventCategory: 'Customer profile activities - Tag Filter',
      eventAction: 'click'
    };
  }

  get gaTracking() {
    return {
      period: {
        eventCategory: 'Customer profile activities - Period',
        eventAction: 'change'
      }
    };
  }

  mounted() {
    eventBus.$on(
      'reloadCustomerDetailActivities',
      this.reloadCustomerDetailActivities
    );
  }

  beforeDestroy() {
    eventBus.$off(
      'reloadCustomerDetailActivities',
      this.reloadCustomerDetailActivities
    );
  }

  created() {
    this.loadActivities({
      clearList: true,
      showLoader: false,
      isFirstRequest: true
    });
  }

  reloadCustomerDetailActivities() {
    this.loadActivities({
      clearList: true,
      showLoader: false
    });
  }

  isCanceledActivity(status: EActivityStatus) {
    return status === EActivityStatus.CANCELED;
  }

  buildActionMessage(message: string, status: EActivityStatus) {
    return this.isCanceledActivity(status)
      ? `<s>${message}</s><span class="action-status"> (CANCELED)</span>`
      : message;
  }

  formatedActivities(activities: ICustomerActivity[]): ITableRow[] {
    const formatActivity = ({
      activity: { createdAt, initiative, message, label, status }
    }: ICustomerActivity) => {
      const date = DateUtils.formattedDate({
        date: createdAt,
        applyTimezone: true
      });
      const hour = DateUtils.formattedDate({
        date: createdAt,
        format: 'h:mm a',
        applyTimezone: true
      });

      return {
        date: this.isMobileDevice
          ? `${date} • ${hour}`
          : `${date}<br />${hour}`,
        initiativeSlug: initiative,
        initiativeLabel: label,
        action: this.buildActionMessage(message, status),
        status
      };
    };

    return activities.map(formatActivity);
  }

  async loadActivities({
    clearList = false,
    showLoader = true,
    isFirstRequest = false
  } = {}) {
    if (clearList) {
      this.pagination = PaginationUtils.defaultPagination();
    }

    if (
      this.firstRequestIsLoading ||
      !this.customerId ||
      !this.pagination.mustCallApi
    )
      return;

    let endpointPath = `/v2/admin/activity/customer/${this.customerId}`;
    let queryParameters = this.filters;

    if (this.pagination.next) {
      endpointPath = this.pagination.next;
      queryParameters = clearList ? this.filters : {};
    }

    if (showLoader) this.$showLoaderWithLocker();
    if (isFirstRequest) this.firstRequestIsLoading = true;

    const response = await this.performanceOverviewService.getCustomerActivities(
      endpointPath,
      queryParameters
    );

    if (showLoader) this.$hideLoader();
    if (isFirstRequest) this.firstRequestIsLoading = false;

    if (response.status !== 200) return;

    const { items, links, filters } = response.data;

    if (filters) {
      this.initiatives = filters.initiatives.filter(
        applyPerformanceDataFilter(
          this.businessReferralIsActive,
          'type',
          flagKeys.referralProgram
        )
      );
    }

    Object.assign(this.pagination, {
      next: links.next,
      mustCallApi: !!links.next
    });

    if (clearList) {
      this.activities = this.formatedActivities(items);

      return;
    }

    this.activities = [...this.activities, ...this.formatedActivities(items)];
  }

  handleSort(field: string) {
    this.headers = PaginationUtils.getUpdatedHeaders(field, this.headers);

    const sortDir = this.headers[field].sortDir;

    this.$analytics({
      eventAction: 'click',
      eventCategory: 'Customer Profile Activities - Sort',
      eventLabel: `[${sortDir}] ${this.headers[field].label} column`
    });

    this.filters = {
      ...this.filters,
      sort: `activity.${field}:${sortDir}`
    };

    this.loadActivities({ clearList: true });
  }

  handleFilterChange(initiatives: string[]) {
    this.filters = {
      ...this.filters,
      initiatives
    };

    this.loadActivities({ clearList: true });
  }

  handlePeriodChange(period: IPeriod) {
    const newPeriod = PerformanceUtils.normalizedPeriod(period);

    this.period = newPeriod;
    this.filters = {
      ...this.filters,
      from: newPeriod.start,
      to: newPeriod.end
    };

    this.loadActivities({ clearList: true });
  }
}
